/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MapActionsContext, useSelectorWithMapId } from '../map/BaseMapContainer';
import {
  selectMapDimensions,
  selectMapRulerToolFollowAircraft,
  selectMapRulerToolMarkersAndPolylines,
  selectMapRulerToolShown,
  selectMapRulerToolWaypoints,
  selectMapSettingsFtvMapHeight,
  selectOverlaysCurrentContext,
  selectTerminalDetailsRulerData,
} from '../../../store';
import { Box, Divider, IconButton, Stack } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import appTheme from '../theme/appTheme.json';
import { distanceInMiles, milesToKilometers } from '../../../utils/DistanceUtils';
import { secondsToFormattedHoursMinutesSecondsColonDelimitedFormat } from '../../../utils/DateTimeUtils';
import Scrollbars from '../layout/Scrollbars';
import { OverlaysContextsEnum } from '../../../store/maps/reducer';
import { isNil } from 'lodash';
import { calculateTravelTimeSeconds } from '../../../utils/VelocityUtils';
import { EMPTY_FIELD_STRING_PLACEHOLDER } from '../../../utils/StringUtils';
import { LEG_END_INDEX } from '../map/RulerPath';

export type LegInfo = {
  legId: number;
  legDistanceMiles: number;
  legETASeconds: number;
  legStart?: google.maps.LatLng;
  legEnd?: google.maps.LatLng;
};

interface RulerToolDistanceSummaryProps {}

export function RulerToolDistanceSummary(props: RulerToolDistanceSummaryProps) {
  const dispatch = useDispatch();
  const mapActionsContext = useContext(MapActionsContext);
  const mapRulerToolShown = useSelectorWithMapId(selectMapRulerToolShown);
  const mapRulerToolWaypoints = useSelectorWithMapId(selectMapRulerToolWaypoints);
  const ftvSettingsMapHeight = useSelectorWithMapId(selectMapSettingsFtvMapHeight);
  const mapDimensions = useSelectorWithMapId(selectMapDimensions);
  const overlaysCurrentContext = useSelector(selectOverlaysCurrentContext);
  const terminalRulerData = useSelector(selectTerminalDetailsRulerData);
  const followAircraftEnabled = useSelectorWithMapId(selectMapRulerToolFollowAircraft);
  const markersAndPolylines = useSelectorWithMapId(selectMapRulerToolMarkersAndPolylines);

  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  const {
    hideMapRulerTool,
    setLegDataMapRulerTool,
    clearLegDataMapRulerTool,
    removeLatLngInMapRulerTool,
    setMarkersAndPolylinesMapRulerTool,
  } = mapActionsContext.mapActions;

  // Hide the ruler tool when the overlays context changes
  useEffect(() => {
    dispatch(hideMapRulerTool());
  }, [dispatch, hideMapRulerTool, overlaysCurrentContext]);

  // Update the window height when the window is resized
  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Calculate distances between waypoints
  const legDistances = useMemo(() => {
    const distances: LegInfo[] = [];
    if (mapRulerToolShown) {
      if (mapRulerToolWaypoints.length === 1) {
        distances.push({
          legId: 1,
          legDistanceMiles: 0,
          legETASeconds: 0,
          legStart: mapRulerToolWaypoints[0],
        });
      } else if (mapRulerToolWaypoints.length >= 2) {
        for (let i = 0; i < mapRulerToolWaypoints.length - 1; i++) {
          const altitudeFeet = terminalRulerData?.altitudeFeet || 0;
          const distanceMilesWithConstantAltitude = distanceInMiles(
            mapRulerToolWaypoints[i],
            mapRulerToolWaypoints[i + 1],
            altitudeFeet,
          );
          distances.push({
            legId: i + 1,
            legDistanceMiles: distanceMilesWithConstantAltitude,
            legETASeconds:
              followAircraftEnabled && terminalRulerData?.velocityKnots
                ? calculateTravelTimeSeconds(distanceMilesWithConstantAltitude, terminalRulerData.velocityKnots)
                : 0,
            legStart: mapRulerToolWaypoints[i],
            legEnd: mapRulerToolWaypoints[i + 1],
          });
        }
      }
    }
    return distances;
  }, [
    mapRulerToolWaypoints,
    followAircraftEnabled,
    terminalRulerData?.altitudeFeet,
    terminalRulerData?.velocityKnots,
    mapRulerToolShown,
  ]);

  // Calculate total distance of all legs
  const totalDistanceMiles = useMemo(() => {
    return legDistances.reduce((total, leg) => total + leg.legDistanceMiles, 0);
  }, [legDistances]);

  // Calculate total time of all legs
  const totalEtaSeconds = useMemo(() => {
    return legDistances.reduce((total, leg) => total + (leg.legETASeconds || 0), 0);
  }, [legDistances]);

  // Create the distance summary string
  const distanceSummaryString = useMemo(() => {
    return `${totalDistanceMiles.toFixed(1)} mi (${milesToKilometers(totalDistanceMiles, 1)} km)`;
  }, [totalDistanceMiles]);

  // Max height, in pixels, the leg list can expand to
  const legListMaxHeightPixels = useMemo(() => {
    const upperBoundaryHeight = followAircraftEnabled ? 222 : 182;
    let height: number;

    if (overlaysCurrentContext === OverlaysContextsEnum.FTV && !isNil(ftvSettingsMapHeight)) {
      height = ftvSettingsMapHeight - upperBoundaryHeight;
    } else if (!isNil(mapDimensions) && !isNil(mapDimensions.height)) {
      height = parseInt(mapDimensions.height, 10) - upperBoundaryHeight;
    } else {
      const offset = followAircraftEnabled ? 326 : 286;
      height = windowHeight - offset;
    }

    return height;
  }, [overlaysCurrentContext, ftvSettingsMapHeight, mapDimensions, windowHeight, followAircraftEnabled]);

  const removeLastLeg = useCallback(() => {
    if (legDistances.length === 1 && legDistances[0].legDistanceMiles === 0) {
      dispatch(hideMapRulerTool());
    } else {
      // Remove last waypoint in list
      if (mapRulerToolWaypoints.length > 0) {
        // Remove the last LatLng in the list
        dispatch(removeLatLngInMapRulerTool(mapRulerToolWaypoints.length - 1));

        // Update leg data to include all the previous legs except the last one
        dispatch(setLegDataMapRulerTool(legDistances.slice(0, -1)));
      }

      // Update markers and polylines
      if (markersAndPolylines.markers.length > 0) {
        // Remove the last marker from the map
        markersAndPolylines.markers[markersAndPolylines.markers.length - 1].setMap(null);

        // Remove the last polyline from the map
        markersAndPolylines.polylines[markersAndPolylines.polylines.length - 1].setMap(null);

        // Remove the last marker and polyline in their respective arrays
        const updatedMarkersAndPolylines = {
          markers: markersAndPolylines.markers.slice(0, -1),
          polylines: markersAndPolylines.polylines.slice(0, -1),
        };

        /**
         * Remove the end point of the second to last polyline
         * (which will now be the last polyline), if it exists,
         * so that it only has one LatLng in its path i.e. its starting point
         */
        if (updatedMarkersAndPolylines.polylines.length >= 1) {
          // Get the path of the last polyline (which currently has two LatLngs in its path)
          const lastPolylinePath =
            updatedMarkersAndPolylines.polylines[updatedMarkersAndPolylines.polylines.length - 1].getPath();

          // Remove the last/end point of the last polyline
          lastPolylinePath.removeAt(LEG_END_INDEX);

          // Update the path of the last polyline (should only have one LatLng in its path)
          updatedMarkersAndPolylines.polylines[updatedMarkersAndPolylines.polylines.length - 1].setPath(
            lastPolylinePath,
          );
        }

        // Update markers and polylines in Redux
        dispatch(setMarkersAndPolylinesMapRulerTool(updatedMarkersAndPolylines));
      }
    }
  }, [
    mapRulerToolWaypoints,
    dispatch,
    removeLatLngInMapRulerTool,
    legDistances,
    setLegDataMapRulerTool,
    markersAndPolylines,
    setMarkersAndPolylinesMapRulerTool,
    hideMapRulerTool,
  ]);

  // Keep Redux store up-to-date with leg data
  useEffect(() => {
    dispatch(setLegDataMapRulerTool(legDistances));
    return () => {
      dispatch(clearLegDataMapRulerTool());
    };
  }, [legDistances, dispatch, setLegDataMapRulerTool, clearLegDataMapRulerTool]);

  return mapRulerToolShown ? (
    <Stack
      spacing={'8px'}
      sx={{
        position: 'absolute',
        bottom: '50px',
        right: '60px',
        minHeight: '60px',
        width: '275px',
        backgroundColor: appTheme.mptPrimaryBackgroundColor,
        padding: '10px',
        borderRadius: '3px',
        fontSize: '12px',
        fontFamily: appTheme.appFontFamily,
        fontStyle: 'normal',
        color: appTheme.primaryTextColor,
        fontWeight: 400,
        lineHeight: '20px',
        boxSizing: 'border-box',
      }}
    >
      <Box data-test="ruler_tool_distance_summary">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: '14px',
            fontWeight: 700,
          }}
        >
          Measure Distance
          <IconButton onClick={() => dispatch(hideMapRulerTool())} sx={{ padding: '0px' }}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box>Click on the map to create a path.</Box>
        {followAircraftEnabled ? <Box>ETA based on current speed and altitude.</Box> : null}
      </Box>
      {legDistances.length ? (
        <Scrollbars autoHeight autoHeightMax={`${legListMaxHeightPixels}px`}>
          <Box
            data-test="ruler_tool_distance_summary_legs"
            sx={{ display: 'flex', gap: '8px', flexDirection: 'column' }}
          >
            {legDistances.map((leg, index) => {
              // Display the delete icon only for the last leg
              const eligibleForDelete = index === legDistances.length - 1;
              return (
                <Box
                  key={`${leg.legId}-container`}
                  sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', width: '100%' }}
                >
                  {eligibleForDelete ? (
                    <IconButton
                      key={`${leg.legId}-delete_button`}
                      data-test={`${leg.legId}-delete_button`}
                      sx={{ padding: '2px', marginRight: '4px' }}
                      onClick={removeLastLeg}
                    >
                      <DeleteIcon sx={{ fontSize: '14px', color: appTheme.secondaryHeaderTextColor }} />
                    </IconButton>
                  ) : null}
                  <Box
                    key={`${leg.legId}-data_container`}
                    // marginLeft = (horizontal padding) + fontSize + (horizontal margin) of the IconButton
                    sx={{ flexGrow: 1, marginLeft: eligibleForDelete ? '0px' : '22px' }}
                  >
                    <Box key={`${leg.legId}-distance`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Box fontWeight={700}>Leg {leg.legId} Distance:</Box>
                      <Box fontSize={'14px'}>
                        {leg.legDistanceMiles.toFixed(1)} mi ({milesToKilometers(leg.legDistanceMiles, 1)} km)
                      </Box>
                    </Box>
                    {followAircraftEnabled && leg.legETASeconds >= 0 && (
                      <Box key={`${leg.legId}-eta`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box fontWeight={700}>Leg {leg.legId} ETA:</Box>
                        <Box fontSize={'14px'}>
                          {leg.legETASeconds > 0
                            ? secondsToFormattedHoursMinutesSecondsColonDelimitedFormat(leg.legETASeconds)
                            : EMPTY_FIELD_STRING_PLACEHOLDER}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Scrollbars>
      ) : null}
      {legDistances.length > 0 ? (
        <>
          <Divider color={appTheme.mptGray} />
          <Box>
            <Box key={`total-distance`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box fontWeight={700}>Total Distance:</Box>
              <Box fontSize={'14px'}>{distanceSummaryString}</Box>
            </Box>
            {followAircraftEnabled && totalEtaSeconds >= 0 && (
              <Box key={`total-eta`} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box fontWeight={700}>Total ETA:</Box>
                <Box fontSize={'14px'}>
                  {totalEtaSeconds > 0
                    ? secondsToFormattedHoursMinutesSecondsColonDelimitedFormat(totalEtaSeconds)
                    : EMPTY_FIELD_STRING_PLACEHOLDER}
                </Box>
              </Box>
            )}
          </Box>
        </>
      ) : null}
    </Stack>
  ) : null;
}

export default RulerToolDistanceSummary;
